import { useEffect, useRef, useState } from "react";
import { EsmProjectSession, type LegacyEnvelope } from "@proof/legacy";
import type { Project } from "@proof/core";
import { Configuration } from "./Configuration";
import { Designer } from "./Designer";
import { Button, Control } from "./components";
import { AuthoringServicesProvider, type AuthoringServices } from "./services";

export interface EsmProjectEditorProps {
  value?: unknown;
  projectId?: string | number;
  services?: AuthoringServices;
  onChange?: (project: Project, esm: LegacyEnvelope) => void;
  onSave?: (output: {
    project: Project;
    esm: LegacyEnvelope;
  }) => void | Promise<void>;
}

export function EsmProjectEditor(props: EsmProjectEditorProps) {
  const [session, setSession] = useState(() =>
    props.value === undefined
      ? undefined
      : new EsmProjectSession(props.value, props),
  );
  const source = useRef({ value: props.value, projectId: props.projectId });
  const epoch = useRef(0);
  const history = useRef<{ past: Project[]; future: Project[] }>({
    past: [],
    future: [],
  });
  const [, redraw] = useState(0);
  const [mode, setMode] = useState("configuration");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [projectId, setProjectId] = useState("");
  useEffect(
    () => () => {
      epoch.current++;
    },
    [],
  );
  if (
    source.current.value !== props.value ||
    source.current.projectId !== props.projectId
  ) {
    const next =
      props.value === undefined
        ? undefined
        : new EsmProjectSession(props.value, props);
    source.current = { value: props.value, projectId: props.projectId };
    epoch.current++;
    history.current = { past: [], future: [] };
    setSession(next);
    setMessage("");
  }
  const change = (project: Project) => {
    if (!session) return;
    try {
      const previous = session.project;
      session.update(project);
      history.current.past.push(previous);
      history.current.future = [];
      epoch.current++;
      redraw((n) => n + 1);
      props.onChange?.(session.project, session.exportESM());
    } catch (error) {
      setMessage((error as Error).message);
    }
  };
  return (
    <AuthoringServicesProvider services={props.services}>
      <section
        className="proof-authoring integration-editor-window"
        aria-label="ESM project editor"
      >
        <div className="panel padded">
          <h2>ESM project editor</h2>
          <p>
            Load the JSON produced by ProofStudio.Esm, edit the project, and
            return the updated JSON to your application.
          </p>
          <Control label="ESM project ID (optional)">
            <input
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              placeholder="Required for multiple projects"
            />
          </Control>
          <div className="button-row">
            <label className="button">
              Load ESM project JSON
              <input
                className="sr-only"
                aria-label="Load ESM project JSON"
                type="file"
                accept=".json,application/json"
                disabled={busy}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  const ticket = ++epoch.current;
                  try {
                    if (file.size > 10_000_000)
                      throw new Error("ESM JSON exceeds 10 MB");
                    const next = new EsmProjectSession(
                      JSON.parse(await file.text()),
                      { projectId: projectId || props.projectId },
                    );
                    if (ticket !== epoch.current) return;
                    setSession(next);
                    history.current = { past: [], future: [] };
                    setMessage(`Loaded ${file.name}`);
                    props.onChange?.(next.project, next.exportESM());
                  } catch (error) {
                    if (ticket === epoch.current)
                      setMessage((error as Error).message);
                  }
                }}
              />
            </label>
            <Button
              disabled={!session}
              onClick={() => {
                const url = URL.createObjectURL(
                  new Blob([JSON.stringify(session!.exportESM(), null, 2)], {
                    type: "application/json",
                  }),
                );
                const link = document.createElement("a");
                link.href = url;
                link.download = "esm-project.json";
                link.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }}
            >
              Export updated JSON
            </Button>
          </div>
          <p role="status">{busy ? "Saving…" : message}</p>
          {!!session?.warnings.length && (
            <details>
              <summary>Import notes</summary>
              <ul>
                {session.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
        {session && (
          <>
            <Control label="Project name">
              <input
                readOnly={
                  !props.services?.editorPresentation?.allowProjectRename
                }
                value={session.project.name}
                onChange={(e) =>
                  change({ ...session.project, name: e.target.value })
                }
              />
            </Control>
            <div className="button-row">
              <Button onClick={() => setMode("configuration")}>
                Fields and configuration
              </Button>
              <Button onClick={() => setMode("design")}>Document design</Button>
            </div>
            {mode === "configuration" ? (
              <Configuration
                project={session.project}
                onChange={change}
                notify={setMessage}
              />
            ) : (
              <Designer
                project={session.project}
                onChange={change}
                notify={setMessage}
                canUndo={history.current.past.length > 0}
                canRedo={history.current.future.length > 0}
                onUndo={() => {
                  const previous = history.current.past.pop();
                  if (previous) {
                    history.current.future.push(session.project);
                    session.update(previous);
                    epoch.current++;
                    redraw((n) => n + 1);
                    props.onChange?.(session.project, session.exportESM());
                  }
                }}
                onRedo={() => {
                  const next = history.current.future.pop();
                  if (next) {
                    history.current.past.push(session.project);
                    session.update(next);
                    epoch.current++;
                    redraw((n) => n + 1);
                    props.onChange?.(session.project, session.exportESM());
                  }
                }}
              />
            )}
          </>
        )}
        <footer className="integration-editor-footer">
          {" "}
          {props.onSave && (
            <Button
              disabled={!session || busy}
              onClick={async () => {
                const ticket = epoch.current;
                setBusy(true);
                setMessage("");
                try {
                  await props.onSave!({
                    project: session!.project,
                    esm: session!.exportESM(),
                  });
                  if (ticket === epoch.current)
                    setMessage("Project returned to host.");
                } catch (error) {
                  if (ticket === epoch.current)
                    setMessage((error as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save to host
            </Button>
          )}
          <Button
            disabled={busy}
            onClick={() => {
              setSession(
                props.value === undefined
                  ? undefined
                  : new EsmProjectSession(props.value, props),
              );
              history.current = { past: [], future: [] };
              epoch.current++;
              setMessage("Changes cancelled.");
            }}
          >
            Cancel
          </Button>
        </footer>
      </section>
    </AuthoringServicesProvider>
  );
}
